import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header";
import ReportsModal from "../../core/modals/reports_modal";
import { fetchAllLeads, Lead } from "../../api/lead";
import { ApexOptions } from "apexcharts";
import Select from "react-select";
import PageLoadProgressBar from "../../core/data/json/pageLoadProgressBar";
import Chart from "react-apexcharts";
import KpiCard from "../../core/common/KPIcard";
import { useDebounce } from "../../api/usedebounce";
import Tip from "../../core/common/tooltip";
import { components } from "react-select";
import ChartExportControl from "../../core/common/ChartExportControl";
import ScrollToTop from "../../core/common/ScrollToTop";

const TimeFrameValueContainer = (props: any) => {
  return (
    <components.ValueContainer {...props}>
      <i
        className="ti ti-calendar-stats me-2 text-muted"
        style={{ fontSize: "16px" }}
      />
      {props.children}
    </components.ValueContainer>
  );
};
const LeadReport = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [timeFrame, setTimeFrame] = useState("Monthly");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);
  useEffect(() => {
    const fetchLeadsData = async () => {
      try {
        const data = await fetchAllLeads();
        setLeads(data);
      } catch (error) {
        console.error("Error fetching leads:", error);
      }
    };
    fetchLeadsData();
  }, []);

  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;
      let startX = 0;
      let startY = 0;

      // Reset position when modal is shown
      const resetPosition = () => {
        // Reset all position variables
        currentX = 0;
        currentY = 0;
        initialX = 0;
        initialY = 0;
        startX = 0;
        startY = 0;
        isDragging = false;

        // Remove only the inline styles that were added during dragging
        modalDialog.style.position = "";
        modalDialog.style.top = "";
        modalDialog.style.left = "";
        modalDialog.style.transform = "";
        modalDialog.style.margin = "";
        modalDialog.style.transition = "";
        modalDialog.style.width = "";
        modalDialog.style.maxWidth = "";

        // Restore centered class if it was removed
        if (!modalDialog.classList.contains("modal-dialog-centered")) {
          modalDialog.classList.add("modal-dialog-centered");
        }
      };

      // Listen for modal show event - reset when modal is about to show
      const handleShow = () => {
        // Reset immediately when modal starts showing
        resetPosition();
      };

      // Listen for modal shown event - ensure reset after modal is fully displayed
      const handleShown = () => {
        // Double-check reset after modal is fully shown
        resetPosition();
      };

      // Listen for modal hidden event - reset when modal is fully hidden
      const handleHidden = () => {
        resetPosition();
      };

      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("shown.bs.modal", handleShown);
      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      const dragStart = (e: MouseEvent) => {
        // Only allow dragging from header, not from close button or other interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest(".btn-close") ||
          target.closest("button") ||
          target.closest("input") ||
          target.closest("select")
        ) {
          return;
        }

        if (
          e.target === modalHeader ||
          modalHeader.contains(e.target as Node)
        ) {
          isDragging = true;
          modalHeader.style.cursor = "move";

          // Get current position and size of modal dialog
          const rect = modalDialog.getBoundingClientRect();
          startX = rect.left;
          startY = rect.top;

          // Get computed styles to preserve the modal's size
          const computedStyle = getComputedStyle(modalDialog);
          const currentWidth = computedStyle.width;
          const currentMaxWidth = computedStyle.maxWidth;
          const currentMinWidth = computedStyle.minWidth;

          // Remove centered class to allow free movement
          modalDialog.classList.remove("modal-dialog-centered");

          // Set initial position based on current position
          modalDialog.style.position = "fixed";
          modalDialog.style.top = `${startY}px`;
          modalDialog.style.left = `${startX}px`;
          modalDialog.style.margin = "0";
          modalDialog.style.transform = "none";
          modalDialog.style.transition = "none";

          // Preserve the modal's width and max-width to prevent size change
          if (
            currentWidth &&
            currentWidth !== "auto" &&
            currentWidth !== "0px"
          ) {
            modalDialog.style.width = currentWidth;
          }
          if (
            currentMaxWidth &&
            currentMaxWidth !== "none" &&
            currentMaxWidth !== "0px"
          ) {
            modalDialog.style.maxWidth = currentMaxWidth;
          }
          if (
            currentMinWidth &&
            currentMinWidth !== "0px" &&
            currentMinWidth !== "auto"
          ) {
            modalDialog.style.minWidth = currentMinWidth;
          }

          initialX = e.clientX - startX;
          initialY = e.clientY - startY;
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position
        const newX = e.clientX - initialX;
        const newY = e.clientY - initialY;

        // Get modal dialog dimensions
        const rect = modalDialog.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        // Constrain to viewport
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        // Update position
        modalDialog.style.left = `${constrainedX}px`;
        modalDialog.style.top = `${constrainedY}px`;

        // Store current position for next drag
        currentX = constrainedX;
        currentY = constrainedY;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
          modalDialog.style.transition = "";
          // Keep the fixed positioning during drag, but allow reset on next open
        }
      };

      // Add cursor style to header
      modalHeader.style.cursor = "move";
      modalHeader.style.userSelect = "none";

      // Event listeners
      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      // Cleanup function
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("shown.bs.modal", handleShown);
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    // Make all three modals draggable
    const cleanup1 = makeModalDraggable("add_calls");
    const cleanup2 = makeModalDraggable("edit_calls");
    const cleanup3 = makeModalDraggable("delete_calls");
    const cleanup4 = makeModalDraggable("bulk_delete_modal");
    const cleanup5 = makeModalDraggable("import_excel_modal");
    const cleanup6 = makeModalDraggable("download_report");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
      cleanup5?.();
      cleanup6?.();
    };
  }, []);

  useEffect(() => {
    if (leads.length > 0) {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 300);
    }
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    if (!term) return leads;

    return leads.filter(
      (lead) =>
        lead.lead_description?.toLowerCase().includes(term) ||
        lead.leadsource?.LeadSource_name?.toLowerCase().includes(term) ||
        lead.users?.name?.toLowerCase().includes(term)
    );
  }, [debouncedSearch, leads]);

  const getLeadsBySource = useMemo(() => {
    const sourceMap: Record<string, number> = {};

    filteredLeads.forEach((lead) => {
      const source = lead.leadsource?.LeadSource_name || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const labels = Object.keys(sourceMap);
    const series = Object.values(sourceMap);

    return { labels, series };
  }, [filteredLeads]);

  const getLeadsByMonth = useMemo(() => {
    const map: Record<string, number> = {};

    filteredLeads.forEach((lead) => {
      const date = new Date(lead.created_at);
      let key = "";

      if (timeFrame === "Monthly") {
        key = date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      }

      if (timeFrame === "Quarterly") {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        key = `Q${quarter} ${date.getFullYear()}`;
      }

      if (timeFrame === "Yearly") {
        key = `${date.getFullYear()}`;
      }

      map[key] = (map[key] || 0) + 1;
    });

    return Object.entries(map)
      .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
      .map(([x, y]) => ({ x, y }));
  }, [filteredLeads, timeFrame]);
  const totalSourceLeads = useMemo(
    () => getLeadsBySource.series.reduce((a, b) => a + b, 0),
    [getLeadsBySource.series]
  );

  const donutOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "donut",
        animations: {
          enabled: true,
          speed: 600,
        },
      },

      labels: getLeadsBySource.labels,

      colors: [
        "#0092E4",
        "#4A00E5",
        "#00B894",
        "#FFA201",
        "#E41F07",
        "#5A5AFE",
      ],

      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 270,
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "14px",
              },
              value: {
                show: true,
                fontSize: "22px",
                fontWeight: 600,
                formatter: (val) => `${val}`,
              },
              total: {
                show: true,
                label: "Total Leads",
                fontSize: "13px",
                formatter: () => `${totalSourceLeads}`,
              },
            },
          },
        },
      },

      dataLabels: { enabled: false },

      tooltip: {
        y: {
          formatter: (value: number) => {
            const percent = ((value / totalSourceLeads) * 100).toFixed(1);
            return `${value} leads (${percent}%)`;
          },
        },
      },

      legend: {
        position: "bottom",
        fontSize: "13px",
        formatter: (val, opts) => {
          const count = getLeadsBySource.series[opts.seriesIndex];
          const percent = ((count / totalSourceLeads) * 100).toFixed(1);
          return `${val} — ${count} (${percent}%)`;
        },
      },

      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 280 },
          },
        },
      ],
    }),
    [getLeadsBySource, totalSourceLeads]
  );

  const avgLeadsValue =
    getLeadsByMonth.length > 0
      ? Math.round(
        getLeadsByMonth.reduce((sum, d) => sum + d.y, 0) /
        getLeadsByMonth.length
      )
      : 0;
  const maxLeads = useMemo(
    () => Math.max(...getLeadsByMonth.map((m) => m.y), 0),
    [getLeadsByMonth]
  );

  const minLeads = useMemo(
    () => Math.min(...getLeadsByMonth.map((m) => m.y), Infinity),
    [getLeadsByMonth]
  );

  const barOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        height: 275,
        toolbar: { show: false },
        animations: {
          enabled: true,
          speed: 600,
          animateGradually: {
            enabled: true,
            delay: 80,
          },
        },
      },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 6,
        },
      },
      colors: getLeadsByMonth.map((d) =>
        d.y === maxLeads ? "#00B894" : d.y === minLeads ? "#E41F07" : "#00918E"
      ),

      dataLabels: { enabled: false },

      grid: {
        strokeDashArray: 4,
        borderColor: "#e9ecef",
      },

      xaxis: {
        type: "category",
        title: {
          text: timeFrame,
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },

      yaxis: {
        title: {
          text: "Number of Leads",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },

      annotations: {
        yaxis: [
          {
            y: avgLeadsValue,
            borderColor: "#FF9800",
            label: {
              text: `Avg leads: ${avgLeadsValue}`,
              style: {
                color: "#fff",
                background: "#FF9800",
                fontSize: "11px",
              },
            },
          },
        ],
      },

      tooltip: {
        theme: "light",
        shared: false,
        intersect: true,
        y: {
          formatter: (val) => `${val} leads`,
        },
      },

      legend: { show: false },

      responsive: [
        {
          breakpoint: 768,
          options: { chart: { height: 240 } },
        },
      ],
    }),
    [getLeadsByMonth, avgLeadsValue, timeFrame]
  );

  // ================= KPI CALCULATIONS =================

  // Helper dates
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // Total Leads
  const totalLeads = filteredLeads.length;

  // Leads This Month (MTD)
  const leadsThisMonth = useMemo(() => {
    return filteredLeads.filter(
      (lead) => new Date(lead.created_at) >= firstDayOfMonth
    ).length;
  }, [filteredLeads]);

  // Top Lead Source
  const topLeadSource = useMemo(() => {
    if (filteredLeads.length === 0) return { name: "-", count: 0 };

    const sourceMap: Record<string, number> = {};

    filteredLeads.forEach((lead) => {
      const source = lead.leadsource?.LeadSource_name || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const [name, count] = Object.entries(sourceMap).sort(
      (a, b) => b[1] - a[1]
    )[0];

    return { name, count };
  }, [filteredLeads]);

  // Average Leads per Month
  const avgLeadsPerMonth = useMemo(() => {
    if (filteredLeads.length === 0) return 0;

    const dates = filteredLeads.map((l) => new Date(l.created_at));

    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const monthDiff =
      (now.getFullYear() - minDate.getFullYear()) * 12 +
      (now.getMonth() - minDate.getMonth()) +
      1;

    return Math.round(totalLeads / monthDiff);
  }, [filteredLeads, totalLeads]);

  // ================= NEW CHART 1: LEAD STATUS FUNNEL =================

  const leadStatusOrder = useMemo(
    () => [
      { key: "Not_Contacted", label: "Not Contacted" },
      { key: "Contacted", label: "Contacted" },
      { key: "Closed", label: "Closed" },
      { key: "Lost", label: "Lost" },
    ],
    []
  );

  const getFunnelData = useMemo(() => {
    const statusMap: Record<string, number> = {};

    filteredLeads.forEach((lead) => {
      const status = lead.lead_status || "Unknown";
      statusMap[status] = (statusMap[status] || 0) + 1;
    });

    return leadStatusOrder
      .map((item) => ({
        x: item.label,
        y: statusMap[item.key] || 0,
      }))
      .filter((item) => item.y > 0); // Only show stages with leads
  }, [filteredLeads, leadStatusOrder]);

  const funnelOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        height: 350,
        animations: { enabled: true, speed: 600 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 8,
          barHeight: "70%",
          distributed: true,
          dataLabels: { position: "center" },
        },
      },
      colors: ["#00918E", "#00B894", "#FFA201", "#E41F07"],
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val} leads`,
        style: {
          fontSize: "14px",
          fontWeight: 600,
          colors: ["#fff"],
        },
        dropShadow: { enabled: false },
      },
      xaxis: {
        type: "category",
        labels: { show: false },
      },
      yaxis: {
        title: {
          text: "Lead Stages",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      grid: { show: false },
      tooltip: {
        y: { formatter: (val: number) => `${val} leads` },
      },
      title: {
        text: "Lead Status Funnel",
        align: "center",
        style: { fontSize: "15px", fontWeight: 600 },
      },
      legend: { show: false },
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { height: 300 } },
        },
      ],
    }),
    []
  );

  // ================= NEW CHART 2: HEATMAP BY DAY OF WEEK (ALL MONTHS) =================

  const getHeatmapData = useMemo(() => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthMap: Record<string, number[]> = {};

    filteredLeads.forEach((lead) => {
      const date = new Date(lead.created_at);
      const monthKey = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      const dayIndex = date.getDay();

      if (!monthMap[monthKey]) {
        monthMap[monthKey] = [0, 0, 0, 0, 0, 0, 0];
      }
      monthMap[monthKey][dayIndex]++;
    });

    // Sort months chronologically (oldest first)
    const sortedMonths = Object.keys(monthMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return sortedMonths.map((month) => ({
      name: month,
      data: monthMap[month],
    }));
  }, [filteredLeads]);

  // ================= KPI PERCENTAGES =================
  const totalLeadsPercent = "100";

  const leadsThisMonthPercent =
    totalLeads === 0
      ? "0"
      : ((leadsThisMonth / totalLeads) * 100).toFixed(1);

  const topSourcePercent =
    totalLeads === 0
      ? "0"
      : ((topLeadSource.count / totalLeads) * 100).toFixed(1);

  const avgLeadsPercent =
    totalLeads === 0
      ? "0"
      : ((avgLeadsPerMonth / totalLeads) * 100).toFixed(1);


  const heatmapOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "heatmap",
        height: 400,
        toolbar: { show: false },
        animations: { enabled: true },
      },
      dataLabels: { enabled: false },
      colors: ["#008FFB"],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.8,
          radius: 8,
          useFillColorAsStroke: true,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: "#f8f9fa", name: "No leads" },
              { from: 1, to: 5, color: "#E0F7FA", name: "Low" },
              { from: 6, to: 15, color: "#80DEEA", name: "Medium" },
              { from: 16, to: 30, color: "#00ACC1", name: "High" },
              { from: 31, to: 999, color: "#006064", name: "Very High" },
            ],
          },
        },
      },
      xaxis: {
        categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        title: {
          text: "Day of Week",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      yaxis: {
        title: { text: "Month", style: { fontSize: "12px", fontWeight: 500 } },
      },
      title: {
        text: "Lead Activity Heatmap (by Day of Week)",
        align: "center",
        style: { fontSize: "15px", fontWeight: 600 },
      },
      tooltip: {
        y: {
          formatter: (val: number) => (val === 0 ? "No leads" : `${val} leads`),
        },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
      },
    }),
    []
  );

  const timeFrameSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 40,
      borderRadius: 8,
      borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 1px #0d6efd" : "none",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      cursor: "pointer",
    }),
    option: (base: any, state: any) => ({
      ...base,
      padding: "8px 12px",
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
          ? "#f1f5ff"
          : "#fff",
      color: state.isSelected ? "#fff" : "#212529",
      cursor: "pointer",
    }),
    singleValue: (base: any) => ({
      ...base,
      fontWeight: 500,
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#6c757d",
      "&:hover": { color: "#0d6efd" },
    }),
    valueContainer: (base: any) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      paddingLeft: 12,
    }),
  };

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-4">
            <div className="row align-items-center">
              <div className="col-sm-8">
                <h4 className="page-title d-flex align-items-center gap-2">
                  <i className="ti ti-file-invoice text-primary" />
                  Leads Report
                  <Tip
                    text="This report provides an overview of lead generation performance, including trends, sources, and key metrics."
                    placement="right"
                  >
                    <i
                      className="fa fa-info-circle text-primary"
                      style={{ cursor: "pointer", fontSize: "15px" }}
                    />
                  </Tip>
                </h4>

                <small className="text-muted">
                  Total: {filteredLeads.length} Leads
                </small>
              </div>

              <div className="col-sm-4 text-sm-end">
                <div className="head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
          </div>
          {/* ================= KPI CARDS (LeadSource Style + Percentage) ================= */}
          <div className="row g-3 mb-4">
  {(() => {
    const total = totalLeads;

    const leadsThisMonthPercentCalc =
      total > 0 ? ((leadsThisMonth / total) * 100).toFixed(1) : "0";

    const topSourcePercentCalc =
      total > 0 ? ((topLeadSource.count / total) * 100).toFixed(1) : "0";

    const avgLeadsPercentCalc =
      total > 0 ? ((avgLeadsPerMonth / total) * 100).toFixed(1) : "0";

    return (
      <>
        <div className="row g-3">
          {/* TOTAL LEADS */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Total Leads: ${totalLeads} (100%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Leads"
                  subtitle="Total"
                  value={totalLeads}
                  icon="ti ti-users"
                  color="primary"
                  tooltip="Total number of leads in the system"
                />
              </div>
            </Tip>
          </div>

          {/* LEADS THIS MONTH */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Leads This Month: ${leadsThisMonth} (${leadsThisMonthPercentCalc}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Leads"
                  subtitle="This Month"
                  value={leadsThisMonth}
                  icon="ti ti-calendar"
                  color="warning"
                  tooltip="Leads created in the current month"
                  percentage={leadsThisMonthPercentCalc}
                  percentageColor={
                    parseFloat(leadsThisMonthPercentCalc) > 0
                      ? "success"
                      : "danger"
                  }
                  percentageIcon={
                    parseFloat(leadsThisMonthPercentCalc) > 0
                      ? "ti ti-arrow-up"
                      : "ti ti-arrow-down"
                  }
                />
              </div>
            </Tip>
          </div>

          {/* TOP LEAD SOURCE */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Top Lead Source: ${topLeadSource.name} (${topSourcePercentCalc}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Lead Source"
                  subtitle={`Top: ${topLeadSource.name}`}
                  value={topLeadSource.count}
                  icon="ti ti-broadcast"
                  color="success"
                  tooltip="Lead source generating the most leads"
                  percentage={topSourcePercentCalc}
                  percentageColor="success"
                  percentageIcon="ti ti-arrow-up"
                />
              </div>
            </Tip>
          </div>

          {/* AVG LEADS / MONTH */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Avg Leads / Month: ${avgLeadsPerMonth} (${avgLeadsPercentCalc}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Leads"
                  subtitle="Avg / Month"
                  value={avgLeadsPerMonth}
                  icon="ti ti-chart-line"
                  color="info"
                  tooltip="Average leads generated per month"
                  percentage={avgLeadsPercentCalc}
                  percentageColor="info"
                  percentageIcon="ti ti-arrow-up"
                />
              </div>
            </Tip>
          </div>
        </div>
      </>
    );
  })()}
</div>



          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <div className="input-group w-50">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa fa-search text-muted" />
                </span>

                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by source, owner, description… (e.g. Facebook, John)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />

                {searchInput && (
                  <button
                    className="btn btn-light border"
                    onClick={() => setSearchInput("")}
                    title="Clear search"
                  >
                    <i className="ti ti-x" />
                  </button>
                )}
              </div>

              <Select
                className="w-25"
                options={[
                  { value: "", label: "Choose" },
                  { value: "Monthly", label: "Monthly" },
                  { value: "Quarterly", label: "Quarterly" },
                  { value: "Yearly", label: "Yearly" },
                ]}
                defaultValue={{ value: "Monthly", label: "Monthly" }}
                isSearchable={false}
                styles={timeFrameSelectStyles}
                components={{
                  ValueContainer: TimeFrameValueContainer,
                }}
                onChange={(e) => setTimeFrame(e?.value || "Monthly")}
              />
              <Link
                to="download_report"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#download_report"
                style={{
                                position: "relative",
                                overflow: "hidden",
                              }}
              >
                <i className="ti ti-file-download me-2" /> Download Lead Report
                <span
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  background: "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                                  transform: "skewX(-20deg) translateX(-150%)",
                                  animation: "shine 3s infinite",
                                }}
                              />
              </Link>
            </div>
            {debouncedSearch && filteredLeads.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-database-off fs-2 mb-2" />
                <p>No leads found for "{debouncedSearch}"</p>
              </div>
            )}

            <div className="card-body">
              <div className="row g-4">
                {/* Existing: Leads by TimeFrame */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="leads-by-timeframe">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-nowrap">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-chart-bar text-primary" />
                            Leads by {timeFrame}
                            <Tip text="Displays the number of leads created in each period based on the selected timeframe.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            Showing {getLeadsByMonth.length}{" "}
                            {timeFrame.toLowerCase()} periods
                          </small>
                        </div>

                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getLeadsByMonth.map((d) => ({
                              Period: d.x,
                              Leads: d.y,
                            }))}
                            filename={`Leads_by_${timeFrame}`}
                            cardId="leads-by-timeframe"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getLeadsByMonth.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-bar-off fs-2 mb-2" />
                          <p>No data available</p>
                        </div>
                      ) : (
                        <Chart
                          options={barOptions}
                          series={[{ name: "Leads", data: getLeadsByMonth }]}
                          type="bar"
                          height={275}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Existing: Leads by Source */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="leads-by-source">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-nowrap">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-chart-donut text-primary" />
                            Leads by Source
                            <Tip text="Shows the distribution of leads across different lead sources.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            {getLeadsBySource.labels.length} active sources
                          </small>
                        </div>

                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getLeadsBySource.series.map((val, i) => ({
                              Source: getLeadsBySource.labels[i],
                              Count: val,
                            }))}
                            filename="Leads_by_Source"
                            cardId="leads-by-source"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body d-flex align-items-center justify-content-center">
                      {getLeadsBySource.series.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-pie-off fs-2 mb-2" />
                          <p>No source data available</p>
                        </div>
                      ) : (
                        <div style={{ width: "100%", height: "350px" }}>
                          <Chart
                            options={donutOptions}
                            series={getLeadsBySource.series}
                            type="donut"
                            height={350}
                            width="100%"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* NEW: Lead Status Funnel */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="lead-status-funnel">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-activity text-primary" />
                            Lead Status Funnel
                            <Tip text="Visualizes how leads progress through sales stages.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            Current distribution across stages
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getFunnelData.map((d) => ({
                              Stage: d.x,
                              Leads: d.y,
                            }))}
                            filename="Lead_Status_Funnel"
                            cardId="lead-status-funnel"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getFunnelData.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-filter-off fs-2 mb-2" />
                          <p>No stage data available</p>
                        </div>
                      ) : (
                        <Chart
                          options={funnelOptions}
                          series={[{ data: getFunnelData }]}
                          type="bar"
                          height={350}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* NEW: Heatmap by Day of Week */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="lead-activity-heatmap">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-calendar-event text-primary" />
                            Lead Activity Heatmap
                            <Tip text="Shows which days of the week generate the most leads over time.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            {getHeatmapData.length} months of data
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getHeatmapData.map((m) => ({
                              Month: m.name,
                              Sun: m.data[0],
                              Mon: m.data[1],
                              Tue: m.data[2],
                              Wed: m.data[3],
                              Thu: m.data[4],
                              Fri: m.data[5],
                              Sat: m.data[6],
                            }))}
                            filename="Lead_Activity_Heatmap"
                            cardId="lead-activity-heatmap"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getHeatmapData.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-calendar-off fs-2 mb-2" />
                          <p>No activity data available</p>
                        </div>
                      ) : (
                        <Chart
                          options={heatmapOptions}
                          series={getHeatmapData}
                          type="heatmap"
                          height={450}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ReportsModal />
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default LeadReport;
